import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import StepName from './StepName';
import TipsSidebar from './TipsSidebar';
import StepInfo from './StepInfo';
import StepType from './StepType';
import StepVisibility from './StepVisibility';
import { useClient } from 'jsonapi-react';
import * as yup from 'yup';
import { t } from 'i18next';
import { Loader } from '@/components/custom/loader';

import promiseRequest from '@/utils/promiseToast';

export default function QuestionContent(props) {
  const navigate = useNavigate();
  const { step, steps, currentStep, setCurrentStep } = props;
  const [question, setQuestion] = useState();
  const questionId = useParams().id;
  const client = useClient();
  const [questionType, setQuestionType] = useState(t('exams.selectMultipleChoice'));
  const [removedAlternatives, setRemovedAlternatives] = useState([]);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  const [taxonomies, setTaxonomies] = useState([]);
  const [isLoadingTaxonomies, setIsLoadingTaxonomies] = useState(false);
  const { promiseToast, isSubmitting } = promiseRequest();

  const getQuestion = async () => {
    if (!questionId) return;
    setIsLoadingQuestion(true);
    const { data, error } = await client.fetch(['questions', questionId]);
    if (!questionId) {
      setIsLoadingQuestion(false);
      return;
    }
    if (error) {
      toast.error('Falha ao buscar dados da questão');
    } else {
      setQuestion(data);
      if (data.modality === 'right_wrong') {
        setQuestionType(t('exams.selectTrueOrFalse'));
      } else if (data.modality === 'discursive') {
        setQuestionType(t('exams.selectDiscursiveQuestion'));
      }
    }

    setIsLoadingQuestion(false);
  };

  const fetchTaxonomies = async () => {
    setIsLoadingTaxonomies(true);

    try {
      const { data } = await client.fetch('taxonomies');
      setTaxonomies(data);
    } catch (e) {
      console.error(e);
      toast.error(t('toast.errorGetTags'));
    } finally {
      setIsLoadingTaxonomies(false);
    }
  };

  const createQuestionForm = useFormik({
    initialValues: {
      published: false,
      description: '',
      modality: 'Múltipla escolha',

      visibility: '',
      'motivator-texts-attributes': [
        {
          title: '',
          body: '',
          print: ''
        }
      ],
      selectedTaxonomies: [],
      code: '',
      'alternatives-attributes': [
        {
          name: '',
          description: '',
          'is-correct': true
        },
        {
          name: '',
          description: '',
          'is-correct': false
        }
      ],
      authored_by_user: false,
      official_content: false
    },
    validationSchema: yup.object({
      code: yup.string().required(t('warning.requiredField')),
      knowledge: yup.array().min(1, t('warning.requiredField')).nullable(),
      selectedTaxonomies: yup.array().min(1, t('warning.selectTaxonomy')),
      description: yup.string().required(t('warning.requiredField')),
      'alternatives-attributes': yup.array().when('modality', {
        is: 'discursive',
        then: () => yup.array(),
        otherwise: () =>
          yup
            .array()
            .of(
              yup.object().shape({
                description: yup.string().required(t('warning.requiredField')),
                name: yup.string(),
                'is-correct': yup.boolean()
              })
            )
            .min(2, t('warning.minimumTwoAlternatives'))
      })
    }),

    onSubmit: () => handleSubmit
  });

  useEffect(() => {
    if (question) {
      createQuestionForm.setValues({
        published: question?.published,
        description: question?.description,
        modality: question?.modality,

        visibility: question?.visibility,
        'motivator-texts-attributes': question?.['motivator-texts'],
        selectedTaxonomies: question?.taxonomies,
        code: question?.code,
        'alternatives-attributes': question?.['alternatives'],
        official_content: question['official-content'],
        authored_by_user: question['authored-by-user']
      });
    }
  }, [question]);

  useEffect(() => {
    const timer = setTimeout(() => {
      getQuestion();
      fetchTaxonomies();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (createQuestionForm.values.modality === 'right_wrong' && !question) {
      createQuestionForm.setFieldValue('alternatives-attributes', [
        { name: '', description: 'Certo', 'is-correct': true },
        { name: '', description: 'Errado', 'is-correct': false }
      ]);
    } else if (createQuestionForm.values.modality === 'discursive') {
      createQuestionForm.setFieldValue('alternatives-attributes', []);
    } else {
      createQuestionForm.setFieldValue('alternatives-attributes', [
        {
          name: '',
          description: '',
          'is-correct': true
        },
        {
          name: '',
          description: '',
          'is-correct': false
        }
      ]);
    }
  }, [createQuestionForm.values.modality]);

  const createQuestion = async () => {
    const auxValues = { ...createQuestionForm.values };

    auxValues['taxonomy-ids'] = (auxValues.selectedTaxonomies || []).map(t => t.id);

    if (auxValues.visibility === 'exam') {
      auxValues.visibility = 'exam';
    } else if (auxValues.visibility === 'train') {
      auxValues.visibility = 'train';
    } else if (auxValues.visibility === 'mock') {
      auxValues.visibility = 'mock';
    }

    if (questionType === 'Múltipla escolha') {
      auxValues['alternatives-attributes'] = auxValues['alternatives-attributes'].map((item, index) => {
        const alphabet = 'ABCDEFGH';
        return { ...item, name: alphabet[index] };
      });
    }

    const deletedAlternatives = removedAlternatives.map(item => {
      return { ...item, _destroy: true };
    });

    auxValues['alternatives-attributes'] = [...deletedAlternatives, ...auxValues['alternatives-attributes']];

    delete auxValues.selectedTaxonomies;

    if (auxValues['motivator-text-ids']?.length > 0) {
      // Reuso de texto de apoio existente: envia só os ids (anexa via N:N) e não
      // recria nem edita o texto compartilhado por outras questões.
      delete auxValues['motivator-texts-attributes'];
    } else {
      delete auxValues['motivator-text-ids'];
    }

    const path = questionId ? ['questions', questionId] : 'questions';

    promiseToast({
      url: path,
      request: auxValues,
      successText: questionId ? t('toast.successEditQuestion') : t('toast.successCreateQuestion'),
      errorText: t('toast.errorCreateQuestion')
    })
      .then(data => {
        navigate('/avaliacoes/banco-de-questoes');
      })
      .catch(error => {
        if (error?.id === 'visibility') toast.error(error?.title);
      });
  };

  const handleSubmit = e => {
    e.preventDefault();

    switch (step) {
      case 'nome':
        if (questionId) {
          createQuestionForm.setFieldTouched('code', true);
          createQuestionForm.setFieldTouched('selectedTaxonomies', true);
          if (!createQuestionForm?.errors.code && !createQuestionForm?.errors.selectedTaxonomies) {
            navigate(`/questoes/criar/informacoes/${questionId}`);
            setCurrentStep(1);
          }
          break;
        } else {
          createQuestionForm?.setFieldTouched('code', true);
          createQuestionForm?.setFieldTouched('selectedTaxonomies', true);
          if (!createQuestionForm?.errors.code && !createQuestionForm?.errors.selectedTaxonomies) {
            navigate(`/questoes/criar/informacoes`);
            setCurrentStep(1);
          }
          break;
        }
      case 'informacoes':
        if (questionId) {
          createQuestionForm?.setFieldTouched('motivator-texts-attributes[0].body', true);
          createQuestionForm?.setFieldTouched('description', true);
          createQuestionForm?.setFieldTouched('motivator-texts-attributes[0].print', true);
          if (!createQuestionForm?.errors?.['motivator-texts-attributes']?.[0].body && !createQuestionForm?.errors?.description && !createQuestionForm?.errors?.['motivator-texts-attributes[0].print']?.[0]?.print) {
            navigate(`/questoes/criar/tipo/${questionId}`);
            setCurrentStep(2);
          }
          break;
        } else {
          createQuestionForm?.setFieldTouched('motivator-texts-attributes[0].body', true);
          createQuestionForm?.setFieldTouched('description', true);
          createQuestionForm?.setFieldTouched('motivator-texts-attributes[0].print', true);
          if (!createQuestionForm?.errors?.['motivator-texts-attributes']?.[0]?.body && !createQuestionForm.errors.description && !createQuestionForm?.errors?.['motivator-texts-attributes']?.[0]?.print) {
            navigate(`/questoes/criar/tipo`);
            setCurrentStep(2);
          }
          break;
        }
      case 'tipo':
        if (questionId) {
          createQuestionForm?.setFieldTouched('alternatives-attributes', true);
          if (!createQuestionForm?.errors['alternatives-attributes']) {
            navigate(`/questoes/criar/visibilidade/${questionId}`);
            setCurrentStep(3);
          }
          break;
        } else {
          createQuestionForm?.setFieldTouched('alternatives-attributes', true);
          if (!createQuestionForm?.errors['alternatives-attributes']) {
            navigate(`/questoes/criar/visibilidade`);
            setCurrentStep(3);
          }
          break;
        }

      case 'visibilidade':
        createQuestion();

        break;
    }
  };

  const btnsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px'
  };

  return (
    <div className="page page--wrap">
      {isLoadingQuestion ? (
        <Loader />
      ) : (
        <div className="page__col">
          <form
            className="form form--step-by-step"
            method="post"
            onSubmit={handleSubmit}
          >
            <StepName
              question={question}
              step={step}
              form={createQuestionForm}
              taxonomies={taxonomies}
              isLoadingTaxonomies={isLoadingTaxonomies}
            />
            <StepInfo
              step={step}
              form={createQuestionForm}
              question={question}
            />
            <StepType
              setRemovedAlternatives={setRemovedAlternatives}
              removedAlternatives={removedAlternatives}
              questionType={questionType}
              setQuestionType={setQuestionType}
              step={step}
              form={createQuestionForm}
              question={question}
            />
            <StepVisibility
              step={step}
              form={createQuestionForm}
            />

            <div style={btnsStyles}>
              {step !== 'nome' && (
                <button
                  type="button"
                  className="btn btn--outline btn--wide"
                  onClick={() => {
                    if (questionId) {
                      navigate(`/questoes/criar/${steps?.[currentStep - 1]?.route}/${questionId}`);
                      setCurrentStep(currentStep - 1);
                    } else {
                      navigate(`/questoes/criar/${steps?.[currentStep - 1]?.route}`);
                      setCurrentStep(currentStep - 1);
                    }
                  }}
                >
                  {t('exams.previous')}
                </button>
              )}

              <button
                type="submit"
                className="btn btn--primary btn--wide"
                disabled={isSubmitting}
              >
                {step !== 'visibilidade' ? t('button.next') : t('button.finish')}
              </button>
            </div>
          </form>
        </div>
      )}

      <TipsSidebar
        step={step}
        form={createQuestionForm}
      />
    </div>
  );
}
